import { convertToDateWithLocale, formatDateFieldInput, formatDisplayDate } from '@/helpers';
import {
  BaseEntityDTO,
  IBaseEntityDTO,
  ICriteriaDTO,
  ICriteriaGroupDTO,
  ITeamCriteriaDTO,
  IProductDTO,
  CriteriaDTO,
  CriteriaGroupDTO,
  TeamCriteriaDTO,
  ProductDTO,
} from './';

export interface IYearDTO extends IBaseEntityDTO {
  year: number;
  start_date?: Date;
  end_date?: Date;
  launch_date?: Date;
  turn_start?: Date;
  turn_deadline?: Date;
  screening_copy_deadline?: Date;
  theme: string;
  criterias: ICriteriaDTO[];
  criteriagroups: ICriteriaGroupDTO[];
  teamCriteria: ITeamCriteriaDTO[];
  early_bird_reg?: Date;
  registration_text: string;
  registration_level_text: string;
  turn_in_overview_text: string;
  turn_in_upload_movie_text: string;
  turn_in_upload_movie_late: string;
  turn_in_upload_screening_copy_text: string;
  turn_in_entry_text: string;
  turn_in_criteria_text: string;
  turn_in_still_text: string;
  turn_in_release_text: string;
  turn_in_cast_text: string;
  criteria_selection_text: string;
  criteria_faq: string;
  allow_optional_criteria: boolean;
  products: IProductDTO[];
  fieldInputStartDate: string;
  displayStartDate: string;
  fieldInputEndDate: string;
  displayEndDate: string;
  fieldInputLaunchDate: string;
  displayLaunchDate: string;
  fieldInputTurnStart: string;
  displayTurnStart: string;
  fieldInputTurnDeadline: string;
  displayTurnDeadLine: string;
  fieldInputScreeningCopyDeadline: string;
  displayScreeningCopyDeadline: string;
}

export class YearDTO extends BaseEntityDTO implements IYearDTO {
  year: number;
  start_date?: Date;
  end_date?: Date;
  launch_date?: Date;
  turn_start?: Date;
  turn_deadline?: Date;
  screening_copy_deadline?: Date;
  theme: string;
  criterias: ICriteriaDTO[];
  criteriagroups: ICriteriaGroupDTO[];
  teamCriteria: ITeamCriteriaDTO[];
  early_bird_reg?: Date;
  registration_text: string;
  registration_level_text: string;
  turn_in_overview_text: string;
  turn_in_upload_movie_text: string;
  turn_in_upload_movie_late: string;
  turn_in_upload_screening_copy_text: string;
  turn_in_entry_text: string;
  turn_in_criteria_text: string;
  turn_in_still_text: string;
  turn_in_release_text: string;
  turn_in_cast_text: string;
  criteria_selection_text: string;
  criteria_faq: string;
  allow_optional_criteria: boolean;
  products: IProductDTO[];

  constructor(data?: Partial<YearDTO>) {
    super();
    if (data) {
      Object.assign(this, data);
    }
    this.year = data?.year ?? 0;
    this.start_date = data?.start_date ?? undefined;
    this.end_date = data?.end_date ?? undefined;
    this.launch_date = data?.launch_date ?? undefined;
    this.turn_start = data?.turn_start ?? undefined;
    this.turn_deadline = data?.turn_deadline ?? undefined;
    this.screening_copy_deadline = data?.screening_copy_deadline ?? undefined;
    this.theme = data?.theme ?? '';
    this.early_bird_reg = data?.early_bird_reg ?? undefined;
    this.registration_text = data?.registration_text ?? '';
    this.registration_level_text = data?.registration_level_text ?? '';
    this.criterias = data?.criterias ? data.criterias.map((crit) => new CriteriaDTO(crit)) : [];
    this.criteriagroups = data?.criteriagroups
      ? data.criteriagroups.map((crit) => new CriteriaGroupDTO(crit))
      : [];
    this.teamCriteria = data?.teamCriteria
      ? data.teamCriteria.map((crit) => new TeamCriteriaDTO(crit))
      : [];
    this.products = data?.products ? data.products.map((product) => new ProductDTO(product)) : [];
    this.turn_in_overview_text = data?.turn_in_overview_text ?? '';
    this.turn_in_upload_movie_text = data?.turn_in_upload_movie_text ?? '';
    this.turn_in_upload_movie_late = data?.turn_in_upload_movie_late ?? '';
    this.turn_in_upload_screening_copy_text = data?.turn_in_upload_screening_copy_text ?? '';
    this.turn_in_entry_text = data?.turn_in_entry_text ?? '';
    this.turn_in_criteria_text = data?.turn_in_criteria_text ?? '';
    this.turn_in_still_text = data?.turn_in_still_text ?? '';
    this.turn_in_release_text = data?.turn_in_release_text ?? '';
    this.turn_in_cast_text = data?.turn_in_cast_text ?? '';
    this.criteria_selection_text = data?.criteria_selection_text ?? '';
    this.criteria_faq = data?.criteria_faq ?? '';
    this.allow_optional_criteria = data?.allow_optional_criteria ?? false;
  }

  /** EXTENDED Properties **/
  get fieldInputStartDate(): string {
    return this.start_date ? formatDateFieldInput(this.start_date) : '';
  }

  set fieldInputStartDate(value: string) {
    this.start_date = convertToDateWithLocale(value);
  }

  get displayStartDate(): string {
    return this.start_date ? formatDisplayDate(this.start_date) : '';
  }

  get fieldInputEndDate(): string {
    return this.end_date ? formatDateFieldInput(this.end_date) : '';
  }

  set fieldInputEndDate(value: string) {
    this.end_date = convertToDateWithLocale(value);
  }

  get displayEndDate(): string {
    return this.end_date ? formatDisplayDate(this.end_date) : '';
  }

  /* Launch Date */
  get fieldInputLaunchDate(): string {
    return this.launch_date ? formatDateFieldInput(this.launch_date, true) : '';
  }

  set fieldInputLaunchDate(value: string) {
    this.launch_date = convertToDateWithLocale(value, true);
  }

  get displayLaunchDate(): string {
    return this.launch_date ? formatDisplayDate(this.launch_date, true) : '';
  }

  /* Turn In */
  get fieldInputTurnStart(): string {
    return this.turn_start ? formatDateFieldInput(this.turn_start, true) : '';
  }

  set fieldInputTurnStart(value: string) {
    this.turn_start = convertToDateWithLocale(value, true);
  }

  get displayTurnStart(): string {
    return this.turn_start ? formatDisplayDate(this.turn_start, true) : '';
  }

  /* Turn In Deadline */
  get fieldInputTurnDeadline(): string {
    return this.turn_deadline ? formatDateFieldInput(this.turn_deadline, true) : '';
  }

  set fieldInputTurnDeadline(value: string) {
    this.turn_deadline = convertToDateWithLocale(value, true);
  }

  get displayTurnDeadLine(): string {
    return this.turn_deadline ? formatDisplayDate(this.turn_deadline, true) : '';
  }

  /* Screening Copy Deadline */
  get fieldInputScreeningCopyDeadline(): string {
    return this.screening_copy_deadline
      ? formatDateFieldInput(this.screening_copy_deadline, true)
      : '';
  }

  set fieldInputScreeningCopyDeadline(value: string) {
    this.screening_copy_deadline = convertToDateWithLocale(value, true);
  }

  get displayScreeningCopyDeadline(): string {
    return this.screening_copy_deadline
      ? formatDisplayDate(this.screening_copy_deadline, true)
      : '';
  }
}
